/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */

import { DateTime, Interval } from "luxon";
import { EntityLabel } from "./entity-label";
import { ClassificationLabel } from "./classification-label";
import { GroupingLabel } from "./grouping-label";
import { fetchRetry } from "./fetch-retry";
import { ReviewRequestType } from "./processor-types";
import { logAction, sendCriticalLog } from "./logger";

//const BLANK_IMAGE = "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";

// supported review types
// DocSplitter should be used for CDS (CUSTOM_SPLITTING_PROCESSOR)
// DAI_COMPLEX_CLASSIFICATION should be used for CDC (CUSTOM_CLASSIFICATION_PROCESSOR)
// Entity should be used for CDE (CUSTOM_EXTRACTION_PROCESSOR) and specialized parsers like Invoice
// Firestore is for Firestore-based review requests

export abstract class ReviewRequest {
  type: ReviewRequestType;
  name: string;
  createTime: Date;
  reviewStartTime?: Date;
  reviewDuration?: number;
  reviewWeekDay?: string;
  enqueueDuration: string;
  queue: string;
  labels: EntityLabel[] | GroupingLabel[] | ClassificationLabel[];
  pageImageUrls: Array<string> = [];
  data?: any;
  //thumbnailUrl?: string;
  revisions?: any;

  constructor(
    type: ReviewRequestType,
    name: string,
    createTime = new Date(),
    queue: string,
    labels: EntityLabel[] | GroupingLabel[] | ClassificationLabel[] = [],
    pageImageUrls: Array<string>,
    data: any = {},
    revisions: any,
  ) {
    this.type = type;
    this.name = name;
    this.createTime = createTime;
    this.enqueueDuration = this.getEnqueueDuration();
    this.queue = queue;
    this.labels = labels;
    this.pageImageUrls = pageImageUrls;
    this.data = data;
    this.revisions = revisions;
  }

  abstract id: () => String;

  //abstract getThumbnailUrl(accessToken: string): Promise<string>;

  abstract getContent(accessToken: string): Promise<any>;

  abstract saveContent(
    accessToken: string,
    idToken: string,
    content: string,
    data: any,
    rejectReason: string,
  ): Promise<void>;

  abstract resultBaseName(): string;

  getEnqueueDuration(): string {
    const now = DateTime.now();
    const duration = Interval.fromDateTimes(
      DateTime.fromJSDate(this.createTime),
      now,
    ).toDuration(["hours", "minutes", "seconds"]);
    return duration.toFormat("hh'h' mm'm' ss's'");
  }

  suggestedName(): string {
    return this.resultBaseName() + "-reviewed.json";
  }
}

export class FileReviewRequest extends ReviewRequest {
  inputFileSystemFileHandle: FileSystemFileHandle;
  outputFileSystemFileHandlePromise!: Promise<FileSystemFileHandle>;

  constructor(
    fileSystemFileHandle: FileSystemFileHandle,
    type: ReviewRequestType,
    name: string,
    createTime: Date,
    queue: string,
    labels: EntityLabel[] | GroupingLabel[] = [],
    pageImageUrls: Array<string> = [],
  ) {
    super(type, name, createTime, queue, labels, pageImageUrls);
    this.inputFileSystemFileHandle = fileSystemFileHandle;
  }

  id = (): String => {
    return this.queue + this.name;
  };

  resultBaseName(): string {
    return this.name.replace(".json", "");
  }

  async getContent(_accessToken: string = "") {
    const file = await this.inputFileSystemFileHandle.getFile();
    const content = await file.text();
    return JSON.parse(content);
  }

  async saveContent(
    _accessToken: string,
    _idToken: string,
    content: string,
    _data: any,
    _rejectReason: string,
  ) {
    const outputFileSystemFileHandle =
      await this.outputFileSystemFileHandlePromise;
    // create a FileSystemWritableFileStream to write to
    const writableStream = await outputFileSystemFileHandle.createWritable();
    // write to file
    await writableStream.write(content);
    // close the file and write the contents to disk.
    await writableStream.close();
  }

  //override getThumbnailUrl = async (_accessToken: string): Promise<string> => {
  //  if (this.pageImageUrls && this.pageImageUrls.length > 0) {
  //    this.thumbnailUrl = this.pageImageUrls[0];
  //    return this.thumbnailUrl;
  //  } else if (this.thumbnailUrl) {
  //    return this.thumbnailUrl;
  //  } else {
  //    const json = await this.getContent();
  //    if (json.pages?.length > 0 && json.pages[0]?.image?.content) {
  //      this.thumbnailUrl = `data:${json.pages[0]?.image?.mime_type};base64,${json.pages[0]?.image?.content}`;
  //      return this.thumbnailUrl;
  //    } else {
  //      // return blank image
  //      return BLANK_IMAGE;
  //    }
  //  }
  //};
}

export class FirestoreReviewRequest extends ReviewRequest {
  taskId: string;
  documentId: string;
  envelopeId: string;
  processorType: string;
  priority: number;
  firestoreDb: any;
  userId: string;
  extendDeadlineWorker?: Worker;
  expiresAt?: Date;
  expirationWarningTimer?: number;
  _sourceLogged?: boolean;
  _cachedSource?: string;
  _cachedBusinessArea?: string;
  _cachedDocumentId?: string;
  _preloadedContent?: any;

  // Document cache for performance optimization
  private static documentCache = new Map<string, any>();
  private static cacheExpiry = new Map<string, number>();
  private static readonly CACHE_TTL = 10 * 60 * 1000; // 10 minutes

  constructor(taskData: any, firestoreDb: any, userId: string) {
    // Extract page URLs from the correct location in Firestore data
    let pageUrls: string[] = [];

    // Keep empty slots for pages beyond the image generation cap (merged chunks
    // >300 pages) so the page list length matches the document page count. The
    // AI UI component will show a broken image placeholder for empty URLs, but
    // the page navigation and entity overlays still work.

    // First try to get from processed_data.optimized_pages (new format)
    if (taskData.documentData?.processed_data?.optimized_pages) {
      pageUrls = taskData.documentData.processed_data.optimized_pages.map(
        (page: any) => {
          return page.fullUrl || page.previewUrl || "";
        },
      );
    }
    // Fallback to documentData.pages (existing format)
    else if (taskData.documentData?.pages) {
      pageUrls = taskData.documentData.pages.map((p: any) => {
        if (typeof p === "string") {
          return p;
        } else if (p && typeof p.fullUrl === "string") {
          return p.fullUrl;
        } else if (p && typeof p.previewUrl === "string") {
          return p.previewUrl;
        } else {
          return "";
        }
      });
    }

    // Extract fields from processed_data
    const processedData = taskData.documentData?.processed_data || {};

    // Pad pageUrls to match pageCount. For merged chunk documents over the
    // image generation cap (300 pages), the backend only passes real image
    // entries via Pub/Sub to avoid the workflow memory limit. Pad with empty
    // strings so the page navigation reflects the true document length.
    const totalPageCount = processedData.pageCount || 0;
    if (totalPageCount > pageUrls.length) {
      const padding = new Array(totalPageCount - pageUrls.length).fill("");
      pageUrls = pageUrls.concat(padding);
    }

    super(
      "Firestore" as ReviewRequestType,
      processedData.document_id && processedData.document_id !== ""
        ? processedData.document_id
        : processedData.envelope_id || "unknown",
      taskData.createdAt?.toDate() || new Date(),
      processedData.queue_name || "unknown",
      [], // labels will be loaded from task data
      pageUrls,
      taskData,
      taskData.revisions,
    );

    this.taskId = taskData.taskId;
    this.documentId =
      processedData.document_id || processedData.envelope_id || "unknown";
    this.envelopeId = processedData.envelope_id || "unknown";
    this.processorType = processedData.processorType || "unknown";
    this.priority = taskData.priority;
    this.firestoreDb = firestoreDb;
    this.userId = userId;

    // Store expiration time if available
    if (taskData.expiresAt) {
      this.expiresAt = taskData.expiresAt.toDate
        ? taskData.expiresAt.toDate()
        : new Date(taskData.expiresAt);
      this.setupExpirationWarning();
    }
  }

  id = (): string => {
    return this.taskId;
  };

  resultBaseName(): string {
    return `${this.documentId}_${this.processorType}`;
  }

  /**
   * Sets up warnings for task expiration
   * Logs when task is approaching expiration (45 min mark) and when expired
   */
  private setupExpirationWarning(): void {
    if (!this.expiresAt) return;

    const now = Date.now();
    const expiresAtMs = this.expiresAt.getTime();
    const timeUntilExpiration = expiresAtMs - now;

    // If already expired, log immediately
    if (timeUntilExpiration <= 0) {
      console.warn(`⚠️ Task ${this.taskId} has already expired!`);
      sendCriticalLog({
        timestamp: new Date().toISOString(),
        severity: "ERROR",
        userId: this.userId,
        action: "Task already expired on assignment",
        additionalInfo: {
          taskId: this.taskId,
          queue: this.queue,
          envelopeId: this.envelopeId,
          processorType: this.processorType,
          expiresAt: this.expiresAt.toISOString(),
          expiredMinutesAgo: Math.abs(Math.floor(timeUntilExpiration / 60000)),
        },
      });
      return;
    }

    // Log initial expiration info
    const expiresInMinutes = Math.floor(timeUntilExpiration / 60000);
    console.log(
      `Task ${this.taskId} expires in ${expiresInMinutes} minutes (at ${this.expiresAt.toLocaleTimeString()})`,
    );

    // Set warning at 45-minute mark (15 minutes before expiration)
    const warningTime = timeUntilExpiration - 15 * 60 * 1000;
    if (warningTime > 0) {
      this.expirationWarningTimer = window.setTimeout(async () => {
        console.warn(`⚠️ Task ${this.taskId} will expire in 15 minutes!`);
        await sendCriticalLog({
          timestamp: new Date().toISOString(),
          severity: "WARNING",
          userId: this.userId,
          action: "Task approaching expiration",
          additionalInfo: {
            taskId: this.taskId,
            queue: this.queue,
            envelopeId: this.envelopeId,
            processorType: this.processorType,
            expiresAt: this.expiresAt.toISOString(),
            minutesUntilExpiration: 15,
          },
        });
      }, warningTime);
    }

    // Set critical alert at expiration time
    window.setTimeout(async () => {
      console.error(`❌ Task ${this.taskId} has expired!`);
      await sendCriticalLog({
        timestamp: new Date().toISOString(),
        severity: "ERROR",
        userId: this.userId,
        action: "Task expired while user was reviewing",
        additionalInfo: {
          taskId: this.taskId,
          queue: this.queue,
          envelopeId: this.envelopeId,
          processorType: this.processorType,
          expiresAt: this.expiresAt.toISOString(),
          reviewDurationMinutes: this.reviewDuration
            ? Math.floor(this.reviewDuration / 60)
            : 0,
        },
      });
    }, timeUntilExpiration);
  }

  // Cache management methods
  private static isValidCacheEntry(cacheKey: string): boolean {
    const expiry = FirestoreReviewRequest.cacheExpiry.get(cacheKey);
    return expiry ? Date.now() < expiry : false;
  }

  private static setCacheEntry(cacheKey: string, data: any): void {
    FirestoreReviewRequest.documentCache.set(cacheKey, data);
    FirestoreReviewRequest.cacheExpiry.set(
      cacheKey,
      Date.now() + FirestoreReviewRequest.CACHE_TTL,
    );
  }

  private static getCacheEntry(cacheKey: string): any | null {
    if (FirestoreReviewRequest.isValidCacheEntry(cacheKey)) {
      return FirestoreReviewRequest.documentCache.get(cacheKey);
    }
    // Clean up expired entry
    FirestoreReviewRequest.documentCache.delete(cacheKey);
    FirestoreReviewRequest.cacheExpiry.delete(cacheKey);
    return null;
  }

  // Enhanced image preloading with adaptive batching for performance
  static async preloadImages(
    imageUrls: string[],
    accessToken: string,
    batchSize?: number,
  ): Promise<void> {
    if (imageUrls.length === 0) return;

    // Adaptive batch size based on number of images and estimated network conditions
    const adaptiveBatchSize =
      batchSize ||
      FirestoreReviewRequest.calculateOptimalBatchSize(imageUrls.length);

    console.log(
      `🖼️ Starting adaptive batch preload of ${imageUrls.length} images (batch size: ${adaptiveBatchSize})`,
    );

    // Priority loading: First 2 images immediately for instant UI feedback
    if (imageUrls.length > 0) {
      console.log(`🚀 Priority loading first 2 images for immediate display`);
      const priorityUrls = imageUrls.slice(0, 2);
      const priorityPromises = priorityUrls.map((url) =>
        FirestoreReviewRequest.preloadSingleImage(url, accessToken),
      );
      await Promise.allSettled(priorityPromises);
    }

    // Continue with remaining images in optimized batches
    const remainingUrls = imageUrls.slice(2);
    const startTime = performance.now();

    for (let i = 0; i < remainingUrls.length; i += adaptiveBatchSize) {
      const batch = remainingUrls.slice(i, i + adaptiveBatchSize);
      const batchNumber = Math.floor(i / adaptiveBatchSize) + 1;
      console.log(
        `📦 Preloading image batch ${batchNumber}:`,
        batch.length,
        "images",
      );

      // Process batch concurrently with performance monitoring
      const batchStart = performance.now();
      const batchPromises = batch.map((url) =>
        FirestoreReviewRequest.preloadSingleImage(url, accessToken),
      );

      try {
        await Promise.allSettled(batchPromises);
        const batchTime = performance.now() - batchStart;
        console.log(
          `✅ Completed batch ${batchNumber} in ${Math.round(batchTime)}ms`,
        );
      } catch (error) {
        console.warn(`⚠️ Some images failed in batch ${batchNumber}:`, error);
      }

      // Adaptive delay between batches based on performance
      if (i + adaptiveBatchSize < remainingUrls.length) {
        const delay = FirestoreReviewRequest.calculateBatchDelay(batchNumber);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    const totalTime = performance.now() - startTime;
    console.log(
      `🎉 Completed preloading all ${imageUrls.length} images in ${Math.round(totalTime)}ms`,
    );
  }

  private static calculateOptimalBatchSize(totalImages: number): number {
    // Adaptive batch sizing strategy
    if (totalImages <= 5) return 3; // Small documents: smaller batches
    if (totalImages <= 15) return 6; // Medium documents: medium batches
    if (totalImages <= 30) return 8; // Large documents: larger batches
    return 10; // Very large documents: maximum batches
  }

  private static calculateBatchDelay(batchNumber: number): number {
    // Progressive delay reduction as browser cache warms up
    if (batchNumber === 1) return 100; // Longer delay for first batch
    if (batchNumber <= 3) return 50; // Medium delay for early batches
    return 25; // Shorter delay for later batches
  }

  private static preloadSingleImage(
    url: string,
    accessToken: string,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      // Skip empty URLs (pages beyond the image generation cap for merged chunks)
      if (!url) {
        resolve();
        return;
      }

      const img = new Image();

      img.onload = () => {
        console.log(`📷 Preloaded image: ${url.split("/").pop()}`);
        resolve();
      };

      img.onerror = (error) => {
        console.warn(
          `❌ Failed to preload image: ${url.split("/").pop()}`,
          error,
        );
        reject(error);
      };

      // Add access token to image URL for authentication
      const imageUrl = new URL(url);
      imageUrl.searchParams.set("access_token", accessToken);
      img.src = imageUrl.toString();
    });
  }

  async getContent(accessToken: string = "") {
    // Check if content was preloaded
    if (this._preloadedContent) {
      console.log("🚀 PRELOAD HIT: Returning preloaded Document AI JSON");
      return this._preloadedContent;
    }

    // Try multiple possible locations for resultBucket and resultObject
    let resultBucket, resultObject;

    // First try nested processed_data
    const processedData = this.data?.documentData?.processed_data;
    if (processedData?.resultBucket && processedData?.resultObject) {
      resultBucket = processedData.resultBucket;
      resultObject = processedData.resultObject;
    }
    // Then try direct documentData level (Firestore format)
    else if (
      this.data?.documentData?.resultBucket &&
      this.data?.documentData?.resultObject
    ) {
      resultBucket = this.data.documentData.resultBucket;
      resultObject = this.data.documentData.resultObject;
    }

    if (!resultBucket || !resultObject) {
      console.error(
        "CRITICAL: No resultBucket or resultObject found in Firestore task data!",
      );
      throw new Error("Cannot find Document AI result location in GCS");
    }

    // Check cache first
    const cacheKey = `${resultBucket}/${resultObject}`;
    const cachedContent = FirestoreReviewRequest.getCacheEntry(cacheKey);
    if (cachedContent) {
      console.log(
        "🚀 Cache HIT: Returning cached Document AI JSON for:",
        cacheKey,
      );
      return cachedContent;
    }

    console.log(
      "📥 Cache MISS: Fetching Document AI JSON from GCS for:",
      cacheKey,
    );
    const encodedResultObject = String(resultObject)
      .split("/")
      .map(encodeURIComponent)
      .join("/");
    const url = `https://storage.googleapis.com/${resultBucket}/${encodedResultObject}`;

    try {
      const response = await fetchRetry(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: "application/json",
        },
      }, 600, 2);

      if (!response.ok) {
        throw new Error(
          `Failed to fetch Document AI JSON from GCS: ${response.status} ${response.statusText}`,
        );
      }

      const jsonContent = await response.json();

      // Cache the result
      FirestoreReviewRequest.setCacheEntry(cacheKey, jsonContent);

      console.log(
        "✅ Successfully fetched and cached Document AI JSON from GCS",
      );
      console.log(
        "Document AI JSON structure - top level keys:",
        Object.keys(jsonContent),
      );
      console.log(
        "Has pages:",
        !!jsonContent.pages,
        "Pages count:",
        jsonContent.pages?.length || 0,
      );
      console.log(
        "Has entities:",
        !!jsonContent.entities,
        "Entities count:",
        jsonContent.entities?.length || 0,
      );

      return jsonContent;
    } catch (error) {
      console.error(
        "CRITICAL: Failed to fetch Document AI JSON from GCS:",
        error,
      );
      throw error;
    }
  }

  async saveContent(
    accessToken: string,
    idToken: string,
    content: string,
    data: any,
    rejectReason: string,
  ) {
    // Implement PubSub-equivalent behaviors for Firestore tasks

    console.log(
      `Processing completed Firestore task ${this.taskId} (similar to PubSub message acknowledgement)`,
    );

    try {
      // Get environment and project info
      const hitlApp = document.querySelector("hitl-app") as any;
      const environment = hitlApp?.detectedEnvironment || "unknown";
      const databaseId = `${environment}-hitl-tasks`;

      // Get project ID from environment
      // Extract environment prefix (everything before the last part after final dash)
      const projectMatch = environment.match(/^(.+)-\d+$/);
      const projectId = process.env.PROJECT_ID;

      // Get the processed_data that contains handle_prediction_result output + workflow fields
      const processedData = this.data?.documentData?.processed_data || {};

      // Enrich data with processing metadata (similar to PubSub)
      data.reviewDuration = this.reviewDuration || 0;
      data.reviewWeekDay = new Date().toLocaleDateString("de", {
        weekday: "long",
      });
      data.queue = processedData.queue_name || this.queue;
      data.firestoreTaskId = this.taskId;
      data.processingTime = this.reviewDuration || 0;

      // Extract input bucket and object from the stored processed_data
      const inputBucket = processedData.inputBucket || "";
      const inputObject = processedData.inputObject || "";

      console.log("DEBUG: Extracted input fields from processed_data:", {
        inputBucket,
        inputObject,
      });

      // These fields are required by handle_reviewed_result
      data.inputBucket = inputBucket;
      data.inputObject = inputObject;

      // Get GCS storage settings from processed_data
      let outputBucket =
        processedData?.reviewedResultBucket ||
        processedData?.resultBucket ||
        "";
      let resultPrefix = processedData?.resultPrefix || "";

      // Check for callback URL from processed_data
      const callbackUrl = processedData?.callback_url;

      console.log("DEBUG: Checking for processedData and callback URL");
      console.log("DEBUG: Full task data structure:", this.data);
      console.log("DEBUG: documentData:", this.data?.documentData);
      console.log("DEBUG: processedData:", processedData);
      console.log(
        "DEBUG: callback_url from processedData:",
        processedData?.callback_url,
      );
      console.log("DEBUG: Final callbackUrl to use:", callbackUrl);

      // 1. Store results in GCS (similar to PubSub behavior)
      // Track whether GCS save was successful - callback should only be called after successful save
      let gcsSaveSuccessful = false;
      let newResultObjectName = "";

      if (processedData && outputBucket && resultPrefix) {
        // Ensure resultPrefix has trailing slash before concatenation
        const normalizedPrefix = resultPrefix.endsWith("/")
          ? resultPrefix
          : resultPrefix + "/";
        newResultObjectName = normalizedPrefix + this.suggestedName();

        // Determine output bucket based on rejection status
        if (rejectReason && processedData?.rejectedResultBucket) {
          data.storno = rejectReason;
          data.reviewedResult = content;
          content = JSON.stringify({ data });
          outputBucket = processedData.rejectedResultBucket;
        } else if (processedData?.reviewedResultBucket) {
          data.reviewedResult = content;
          content = JSON.stringify({ data });
          outputBucket = processedData.reviewedResultBucket;
        }

        // Store result in GCS if we have bucket and prefix
        if (outputBucket && resultPrefix) {
          console.log(
            `Storing result in GCS: gs://${outputBucket}/${newResultObjectName}`,
          );

          // Log GCS save attempt
          logAction({
            timestamp: new Date().toISOString(),
            severity: "INFO",
            userId: this.userId,
            action: "GCS save attempt",
            additionalInfo: {
              taskId: this.taskId,
              queue: this.queue,
              bucket: outputBucket,
              objectName: newResultObjectName,
              processorType: processedData.processorType,
              isRejection: !!rejectReason,
            },
          });

          // Normalize entities structure for classification tasks before saving
          if (
            processedData.processorType === "CUSTOM_CLASSIFICATION_PROCESSOR"
          ) {
            try {
              const reviewedResult = JSON.parse(content);
              console.log(
                "DEBUG: Review result structure keys:",
                Object.keys(reviewedResult),
              );
              console.log(
                "DEBUG: Full review result structure:",
                reviewedResult,
              );

              // Check if we need to unwrap from data object or access directly
              let entities;
              if (reviewedResult.entities) {
                entities = reviewedResult.entities;
                console.log("DEBUG: Found entities at top level");
              } else if (
                reviewedResult.data &&
                typeof reviewedResult.data === "object"
              ) {
                console.log(
                  "DEBUG: data object keys:",
                  Object.keys(reviewedResult.data),
                );
                console.log(
                  "DEBUG: data object structure:",
                  reviewedResult.data,
                );

                // The entities are in the reviewedResult field inside data
                if (
                  reviewedResult.data.reviewedResult &&
                  typeof reviewedResult.data.reviewedResult === "string"
                ) {
                  try {
                    const actualDocAI = JSON.parse(
                      reviewedResult.data.reviewedResult,
                    );
                    entities = actualDocAI.entities;
                    console.log(
                      "DEBUG: Found entities in data.reviewedResult (parsed)",
                    );
                  } catch (e) {
                    console.log(
                      "DEBUG: Failed to parse reviewedResult, trying direct access",
                    );
                  }
                } else if (
                  reviewedResult.data.reviewedResult &&
                  typeof reviewedResult.data.reviewedResult === "object"
                ) {
                  entities = reviewedResult.data.reviewedResult.entities;
                  console.log(
                    "DEBUG: Found entities in data.reviewedResult (object)",
                  );
                } else {
                  entities = reviewedResult.data.entities;
                  console.log(
                    "DEBUG: Found entities in data object (fallback)",
                  );
                }
              }
              console.log(
                "DEBUG: Original entities before normalization:",
                entities,
              );

              // Normalize classification entities to match splitting structure
              if (entities && entities.length > 0) {
                const normalizedEntities = entities.map((entity) => {
                  if (
                    entity.type === "Document Classification" &&
                    entity.normalized_value?.text
                  ) {
                    // Strip ID prefix from classification value if present
                    // Handle both dash and underscore formats: "1 - Neuschaden", "5_kuendigung", "2 _ beitragsfreistellung"
                    let classificationValue = entity.normalized_value.text;
                    const idPrefixMatch = classificationValue.match(
                      /^(\d+)\s*[-_]\s*(.+)$/,
                    );
                    if (idPrefixMatch) {
                      classificationValue = idPrefixMatch[2];
                      console.log(
                        "DEBUG: Stripped ID prefix from normalized_value.text:",
                        {
                          original: entity.normalized_value.text,
                          stripped: classificationValue,
                        },
                      );
                    }

                    // Move the actual classification value from normalized_value.text to type
                    const normalizedEntity = {
                      ...entity,
                      type: classificationValue,
                      confidence: 1, // Set confidence to 1 for selected classification
                    };
                    // Remove normalized_value to match splitting structure
                    delete normalizedEntity.normalized_value;

                    console.log("DEBUG: Normalized classification entity:", {
                      original_type: entity.type,
                      new_type: normalizedEntity.type,
                      confidence: normalizedEntity.confidence,
                    });

                    return normalizedEntity;
                  }
                  return entity;
                });

                // Set the normalized entities back to the correct location
                if (reviewedResult.entities) {
                  reviewedResult.entities = normalizedEntities;
                } else if (reviewedResult.data) {
                  if (
                    reviewedResult.data.reviewedResult &&
                    typeof reviewedResult.data.reviewedResult === "string"
                  ) {
                    try {
                      const actualDocAI = JSON.parse(
                        reviewedResult.data.reviewedResult,
                      );
                      actualDocAI.entities = normalizedEntities;
                      reviewedResult.data.reviewedResult =
                        JSON.stringify(actualDocAI);
                    } catch (e) {
                      console.warn(
                        "Failed to update normalized entities in parsed reviewedResult",
                      );
                    }
                  } else if (
                    reviewedResult.data.reviewedResult &&
                    typeof reviewedResult.data.reviewedResult === "object"
                  ) {
                    reviewedResult.data.reviewedResult.entities =
                      normalizedEntities;
                  } else {
                    reviewedResult.data.entities = normalizedEntities;
                  }
                }
                console.log(
                  "DEBUG: Normalized entities after processing:",
                  normalizedEntities,
                );
              } else {
                console.log("DEBUG: No entities found to normalize");
              }

              content = JSON.stringify(reviewedResult);
            } catch (error) {
              console.warn(
                "Failed to normalize classification entities, using original content:",
                error,
              );
            }
          }

          // Fixed: Properly encode path components separately to avoid encoding slashes
          const pathComponents = newResultObjectName.split("/");
          const encodedPath = pathComponents
            .map((component) => encodeURIComponent(component))
            .join("/");
          const gcsUrl = `https://storage.googleapis.com/${outputBucket}/${encodedPath}`;

          const gcsResponse = await fetch(gcsUrl, {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
            body: content,
          });

          if (!gcsResponse.ok) {
            const errorText = await gcsResponse
              .text()
              .catch(() => "Unknown error");
            console.error(
              `Failed to save reviewed result to GCS: ${gcsResponse.status} ${gcsResponse.statusText}`,
              errorText,
            );

            // Log critical GCS save failure
            await sendCriticalLog({
              timestamp: new Date().toISOString(),
              severity: "ERROR",
              userId: this.userId,
              action: "GCS save failed",
              additionalInfo: {
                taskId: this.taskId,
                queue: this.queue,
                bucket: outputBucket,
                objectName: newResultObjectName,
                httpStatus: gcsResponse.status,
                httpStatusText: gcsResponse.statusText,
                errorDetails: errorText,
                processorType: processedData.processorType,
              },
            });

            throw new Error(
              `GCS save failed with status ${gcsResponse.status}: ${gcsResponse.statusText}`,
            );
          }

          console.log(
            `Successfully saved reviewed result to GCS: gs://${outputBucket}/${newResultObjectName}`,
          );
          gcsSaveSuccessful = true;

          // Log successful GCS save
          logAction({
            timestamp: new Date().toISOString(),
            severity: "INFO",
            userId: this.userId,
            action: "GCS save successful",
            additionalInfo: {
              taskId: this.taskId,
              queue: this.queue,
              bucket: outputBucket,
              objectName: newResultObjectName,
              processorType: processedData.processorType,
            },
          });
        }

        // 2. Call callback URL only after successful GCS save
        // This prevents the workflow from continuing without the reviewed result being available
        if (callbackUrl && gcsSaveSuccessful) {
          await this.handleWorkflowCallback(
            { callback_url: callbackUrl },
            outputBucket,
            newResultObjectName,
            data,
            content,
            accessToken,
            idToken,
          );
        } else if (callbackUrl && !gcsSaveSuccessful) {
          console.error(
            "Cannot call workflow callback - GCS save was not successful",
          );

          // Log critical callback skip due to GCS save failure
          await sendCriticalLog({
            timestamp: new Date().toISOString(),
            severity: "ERROR",
            userId: this.userId,
            action: "Callback skipped - GCS save failed",
            additionalInfo: {
              taskId: this.taskId,
              queue: this.queue,
              callbackUrl: callbackUrl,
              processorType: processedData.processorType,
            },
          });

          throw new Error(
            "GCS save failed - cannot continue workflow without saved reviewed result",
          );
        } else {
          console.log("No callback URL found - skipping workflow notification");
        }
      } else {
        // Log warning if GCS save was skipped due to missing configuration
        console.warn("GCS save skipped - missing required configuration:", {
          hasProcessedData: !!processedData,
          outputBucket: outputBucket || "(empty)",
          resultPrefix: resultPrefix || "(empty)",
        });

        // Log warning about missing configuration
        logAction({
          timestamp: new Date().toISOString(),
          severity: "WARNING",
          userId: this.userId,
          action: "GCS save skipped - missing configuration",
          additionalInfo: {
            taskId: this.taskId,
            queue: this.queue,
            hasProcessedData: !!processedData,
            hasOutputBucket: !!outputBucket,
            hasResultPrefix: !!resultPrefix,
            hasCallbackUrl: !!callbackUrl,
          },
        });

        // If there's a callback URL but we couldn't save, this is an error
        if (callbackUrl) {
          console.error(
            "Cannot call workflow callback - GCS save was skipped due to missing outputBucket or resultPrefix",
          );

          // Log critical error for callback with missing config
          await sendCriticalLog({
            timestamp: new Date().toISOString(),
            severity: "ERROR",
            userId: this.userId,
            action: "Callback blocked - missing GCS configuration",
            additionalInfo: {
              taskId: this.taskId,
              queue: this.queue,
              callbackUrl: callbackUrl,
              hasOutputBucket: !!outputBucket,
              hasResultPrefix: !!resultPrefix,
            },
          });

          throw new Error(
            "Missing GCS configuration (outputBucket or resultPrefix) - cannot save reviewed result",
          );
        }
      }

      // 3. Delete the task document from Firestore (equivalent to PubSub acknowledgement)
      const collectionName = `hitl_tasks_${this.queue}`;
      const taskDocUrl = `${process.env.INTERNAL_API_URL}/documents/${collectionName}/${this.taskId}`;

      console.log(`Attempting to delete Firestore task: ${taskDocUrl}`);
      console.log(
        `Project ID: ${projectId}, Database ID: ${databaseId}, Collection: ${collectionName}`,
      );

      const deleteResponse = await fetch(taskDocUrl, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!deleteResponse.ok) {
        console.error(
          `Failed to delete Firestore task ${this.taskId}: ${deleteResponse.status} ${deleteResponse.statusText}`,
        );
        console.error(`Task URL: ${taskDocUrl}`);

        // Log critical Firestore deletion failure
        await sendCriticalLog({
          timestamp: new Date().toISOString(),
          severity: "ERROR",
          userId: this.userId,
          action: "Firestore task deletion failed",
          additionalInfo: {
            taskId: this.taskId,
            queue: this.queue,
            collectionName: collectionName,
            httpStatus: deleteResponse.status,
            httpStatusText: deleteResponse.statusText,
            taskUrl: taskDocUrl,
          },
        });

        // For now, log the error but don't fail the entire save operation
        // The task will remain in Firestore and may be picked up again, but the results are saved
        console.warn(
          `Warning: Task ${this.taskId} could not be deleted from Firestore. Results have been saved successfully.`,
        );
        console.warn(
          `This may result in the task being processed again if it has not expired.`,
        );
      } else {
        console.log(
          `Successfully deleted Firestore task ${this.taskId} after processing`,
        );

        // Log successful Firestore deletion
        logAction({
          timestamp: new Date().toISOString(),
          severity: "INFO",
          userId: this.userId,
          action: "Firestore task deleted successfully",
          additionalInfo: {
            taskId: this.taskId,
            queue: this.queue,
            collectionName: collectionName,
          },
        });

        // Emit custom event to trigger queue count refresh
        const event = new CustomEvent("task-completed", {
          bubbles: true,
          detail: {
            taskId: this.taskId,
            queueName: this.queue,
          },
        });
        document.dispatchEvent(event);
      }

      // Stop deadline extension worker (similar to PubSub ack extension termination)
      if (this.extendDeadlineWorker) {
        this.extendDeadlineWorker.terminate();
        console.log(
          `Terminated deadline extension worker for task ${this.taskId}`,
        );
      }

      // Clear expiration warning timer
      if (this.expirationWarningTimer) {
        clearTimeout(this.expirationWarningTimer);
        console.log(`Cleared expiration warning timer for task ${this.taskId}`);
      }
    } catch (error) {
      console.error("Failed to complete Firestore task processing:", error);
      throw error;
    }
  }

  /**
   * Handles workflow callback with graceful degradation for timeout scenarios
   */
  private async handleWorkflowCallback(
    callbackInfo: any,
    outputBucket: string,
    newResultObjectName: string,
    data: any,
    content: string,
    accessToken: string,
    idToken: string,
  ): Promise<void> {
    if (!callbackInfo.callback_url) {
      console.log("No callback URL provided - skipping workflow notification");
      return;
    }

    // Get the stored processed_data (which contains handle_prediction_result output + workflow fields)
    const storedProcessedData = this.data?.documentData?.processed_data || {};

    // Parse the reviewed Document AI JSON to get updated entities
    const reviewedResult = JSON.parse(content);
    const reviewedEntities = reviewedResult.entities || [];

    console.log(
      "DEBUG: handleWorkflowCallback - storedProcessedData.processorType:",
      storedProcessedData.processorType,
    );
    console.log(
      "DEBUG: handleWorkflowCallback - reviewedEntities:",
      reviewedEntities,
    );

    // Build minimal processed_data structure matching historical HITL response format
    // Only include the essential fields that handle_prediction_result needs
    const processedData = {
      outputGcsDestination: `gs://${outputBucket}/${newResultObjectName}`,
      processor_type:
        storedProcessedData.processorType || "CUSTOM_SPLITTING_PROCESSOR",
      // Include reviewed entities for the workflow
      entities: reviewedEntities,
    };

    // For classification tasks, also include the selected classification value
    if (
      storedProcessedData.processorType === "CUSTOM_CLASSIFICATION_PROCESSOR" &&
      reviewedEntities.length > 0
    ) {
      console.log(
        "DEBUG: Processing classification entities before callback:",
        reviewedEntities,
      );

      // For classification tasks, the actual value is in normalized_value.text, not type
      const selectedEntity = reviewedEntities.find(
        (entity) =>
          entity.type === "Document Classification" &&
          entity.normalized_value?.text,
      );

      if (selectedEntity?.normalized_value?.text) {
        processedData.classification = selectedEntity.normalized_value.text;
        console.log(
          "DEBUG: Added classification to callback from normalized_value.text:",
          selectedEntity.normalized_value.text,
        );
      }
    }

    // This matches the historical response format that the workflow expects
    const callbackData = {
      status: "success",
      processed_data: processedData,
    };

    console.log(`Attempting callback to: ${callbackInfo.callback_url}`);
    console.log(
      "Callback data structure:",
      JSON.stringify(callbackData, null, 2),
    );

    try {
      // Use shorter timeout for workflow callbacks to detect expired workflows quickly
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

      const response = await fetch(callbackInfo.callback_url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(callbackData),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        console.log("✅ Workflow callback successful");

        // Log successful callback
        logAction({
          timestamp: new Date().toISOString(),
          severity: "INFO",
          userId: this.userId,
          action: "Workflow callback successful",
          additionalInfo: {
            taskId: this.taskId,
            queue: this.queue,
            callbackUrl: callbackInfo.callback_url,
            processorType: storedProcessedData.processorType,
          },
        });
      } else if (response.status === 404 || response.status === 410) {
        console.warn(
          "⚠️  Workflow has expired (404/410) - using fallback completion",
        );

        // Log workflow expiration
        await sendCriticalLog({
          timestamp: new Date().toISOString(),
          severity: "WARNING",
          userId: this.userId,
          action: "Workflow expired - using fallback",
          additionalInfo: {
            taskId: this.taskId,
            queue: this.queue,
            callbackUrl: callbackInfo.callback_url,
            httpStatus: response.status,
            processorType: storedProcessedData.processorType,
          },
        });

        await this.handleExpiredWorkflowFallback(
          outputBucket,
          newResultObjectName,
          data,
          accessToken,
          idToken,
        );
      } else {
        // Log callback failure with non-timeout error
        await sendCriticalLog({
          timestamp: new Date().toISOString(),
          severity: "ERROR",
          userId: this.userId,
          action: "Workflow callback failed",
          additionalInfo: {
            taskId: this.taskId,
            queue: this.queue,
            callbackUrl: callbackInfo.callback_url,
            httpStatus: response.status,
            httpStatusText: response.statusText,
            processorType: storedProcessedData.processorType,
          },
        });

        throw new Error(
          `Callback failed with status ${response.status}: ${response.statusText}`,
        );
      }
    } catch (error) {
      console.warn("⚠️  Workflow callback failed:", error);

      // Determine if this looks like a workflow timeout scenario
      const isTimeoutError =
        error.name === "AbortError" ||
        error.message?.includes("timeout") ||
        error.message?.includes("network") ||
        error.message?.includes("fetch");

      if (isTimeoutError) {
        console.log(
          "🔄 Detected workflow timeout - using fallback completion mechanism",
        );

        // Log workflow timeout
        await sendCriticalLog({
          timestamp: new Date().toISOString(),
          severity: "WARNING",
          userId: this.userId,
          action: "Workflow callback timeout - using fallback",
          additionalInfo: {
            taskId: this.taskId,
            queue: this.queue,
            callbackUrl: callbackInfo.callback_url,
            errorName: error.name,
            errorMessage: error.message,
            processorType: storedProcessedData.processorType,
          },
        });

        await this.handleExpiredWorkflowFallback(
          outputBucket,
          newResultObjectName,
          data,
          accessToken,
          idToken,
        );
      } else {
        console.log(
          "ℹ️  Non-timeout callback error - document still processed successfully",
        );

        // Log non-timeout callback error
        await sendCriticalLog({
          timestamp: new Date().toISOString(),
          severity: "ERROR",
          userId: this.userId,
          action: "Workflow callback error (non-timeout)",
          additionalInfo: {
            taskId: this.taskId,
            queue: this.queue,
            callbackUrl: callbackInfo.callback_url,
            errorName: error.name,
            errorMessage: error.message,
            processorType: storedProcessedData.processorType,
          },
        });
      }

      // Important: Don't throw here - document processing should succeed even if workflow fails
    }
  }

  /**
   * Fallback mechanism for when workflows have timed out
   * Stores completion info in a special collection for later processing
   */
  private async handleExpiredWorkflowFallback(
    outputBucket: string,
    newResultObjectName: string,
    data: any,
    accessToken: string,
    idToken: string,
  ): Promise<void> {
    try {
      console.log("📝 Storing completion info for offline workflow processing");

      // Get environment info
      const hitlApp = document.querySelector("hitl-app") as any;
      const environment = hitlApp?.detectedEnvironment || "unknown";
      const databaseId = `${environment}-hitl-tasks`;

      // Extract environment prefix (everything before the last part after final dash)
      const projectMatch = environment.match(/^(.+)-\d+$/);
      const projectId = process.env.PROJECT_ID;

      // Store completion info in a fallback collection for later processing
      const fallbackCollectionUrl = `${process.env.INTERNAL_API_URL}/documents/hitl_fallback_completions`;

      const fallbackData = {
        taskId: this.taskId,
        queueName: this.queue,
        documentId: data.documentId || this.documentId || "",
        processorType: data.processorType || this.processorType || "",
        outputGcsDestination: `gs://${outputBucket}/${newResultObjectName}`,
        completedAt: new Date().toISOString(),
        completedBy: this.userId,
        originalCallbackUrl:
          this.data?.documentData?.processed_data?.callback_url || "",
        processingTime: this.reviewDuration || 0.0,
        status: "callback_failed",
      };

      const fallbackResponse = await fetch(fallbackCollectionUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(fallbackData),
      });

      if (fallbackResponse.ok) {
        console.log(
          "✅ Fallback completion record stored - document processing completed successfully despite workflow timeout",
        );

        // Log successful fallback storage
        logAction({
          timestamp: new Date().toISOString(),
          severity: "INFO",
          userId: this.userId,
          action: "Fallback completion record stored",
          additionalInfo: {
            taskId: this.taskId,
            queue: this.queue,
            outputGcsDestination: `gs://${outputBucket}/${newResultObjectName}`,
            processorType: data.processorType || this.processorType || "",
          },
        });
      } else {
        console.error(
          `❌ Failed to store fallback completion record: ${fallbackResponse.status} ${fallbackResponse.statusText}`,
        );

        // Log fallback storage failure
        await sendCriticalLog({
          timestamp: new Date().toISOString(),
          severity: "ERROR",
          userId: this.userId,
          action: "Fallback completion record storage failed",
          additionalInfo: {
            taskId: this.taskId,
            queue: this.queue,
            httpStatus: fallbackResponse.status,
            httpStatusText: fallbackResponse.statusText,
            fallbackCollectionUrl: fallbackCollectionUrl,
          },
        });
      }
    } catch (fallbackError) {
      console.error(
        "❌ Failed to store fallback completion record:",
        fallbackError,
      );

      // Log fallback storage exception
      await sendCriticalLog({
        timestamp: new Date().toISOString(),
        severity: "ERROR",
        userId: this.userId,
        action: "Fallback completion record exception",
        additionalInfo: {
          taskId: this.taskId,
          queue: this.queue,
          errorName: fallbackError.name,
          errorMessage: fallbackError.message,
        },
      });
      // Still don't throw - document processing should complete
    }
  }
}

export class PubSubReviewRequest extends ReviewRequest {
  projectId: string;
  subscriptionName: string;
  ackId: string;
  messageId: string;
  extendAckIdWorker?: Worker;
  resultBucket: string;
  resultPrefix: string;
  resultObject: string;
  reviewedResultBucket: string;
  rejectedResultBucket: string;
  callbackUrl: string;

  constructor(
    projectId: string,
    subscriptionName: string,
    ackId: string,
    messageId: string,
    resultBucket: string,
    resultPrefix: string,
    resultObject: string,
    reviewedResultBucket: string,
    rejectedResultBucket: string,
    callbackUrl: string,
    type: ReviewRequestType,
    name: string,
    createTime = new Date(),
    queue: string,
    labels: EntityLabel[] | GroupingLabel[] = [],
    pageImageUrls: Array<string> = [],
    data: any,
    revisions: any,
  ) {
    super(
      type,
      name,
      createTime,
      queue,
      labels,
      pageImageUrls,
      data,
      revisions,
    );
    this.projectId = projectId;
    this.subscriptionName = subscriptionName;
    this.ackId = ackId;
    this.messageId = messageId;
    this.resultBucket = resultBucket;
    this.resultPrefix = resultPrefix;
    this.resultObject = resultObject;
    this.reviewedResultBucket = reviewedResultBucket;
    this.rejectedResultBucket = rejectedResultBucket;
    this.callbackUrl = callbackUrl;
  }

  id = (): String => {
    return this.resultBucket + this.resultObject;
  };

  resultBaseName(): string {
    // Remove the result prefix from the result object path
    let basename = this.resultObject;

    // Handle both cases: resultPrefix with and without trailing slash
    const prefixWithSlash = this.resultPrefix.endsWith("/")
      ? this.resultPrefix
      : this.resultPrefix + "/";
    const prefixWithoutSlash = this.resultPrefix.endsWith("/")
      ? this.resultPrefix.slice(0, -1)
      : this.resultPrefix;

    // Try to remove prefix (try both variants)
    if (basename.startsWith(prefixWithSlash)) {
      basename = basename.substring(prefixWithSlash.length);
    } else if (basename.startsWith(prefixWithoutSlash)) {
      basename = basename.substring(prefixWithoutSlash.length);
      // If basename now starts with '/', remove it
      if (basename.startsWith("/")) {
        basename = basename.substring(1);
      }
    }

    // Remove .json extension
    return basename.replace(/\.json$/, "");
  }

  async getContent(accessToken: string): Promise<any> {
    const url =
      "https://storage.googleapis.com/" +
      this.resultBucket +
      "/" +
      this.resultObject;
    const response = await fetch(url, {
      headers: {
        Authorization: "Bearer " + accessToken,
      },
    });
    if (!response.ok) {
      throw response;
    }
    return await response.json();
  }

  //override getThumbnailUrl = async (accessToken: string): Promise<string> => {
  //  if (this.pageImageUrls.length > 0) {
  //    const url = new URL(this.pageImageUrls[0]);
  //    url.searchParams.set("access_token", accessToken);
  //    this.thumbnailUrl = url.toString();
  //    return this.thumbnailUrl;
  //  } else {
  //    const json = await this.getContent(accessToken);
  //    if (json.pages?.length > 0) {
  //      if (json.pages[0]?.image?.content) {
  //        this.thumbnailUrl = `data:${json.pages[0]?.image?.mime_type};base64,${json.pages[0]?.image?.content}`;
  //        return this.thumbnailUrl;
  //      }
  //    }
  //  }
  // return blank image
  //  return BLANK_IMAGE;
  //};

  async saveContent(
    accessToken: string,
    idToken: string,
    content: string,
    data: any,
    rejectReason: string,
  ): Promise<void> {
    // save content to GCS
    // Ensure resultPrefix has trailing slash before concatenation
    const normalizedPrefix = this.resultPrefix.endsWith("/")
      ? this.resultPrefix
      : this.resultPrefix + "/";
    const newResultObjectName = normalizedPrefix + this.suggestedName();
    let url;
    let outputBucket = this.resultBucket; // default
    if (
      typeof this.reviewDuration === "undefined" ||
      Number.isNaN(this.reviewDuration)
    ) {
      this.reviewDuration =
        (new Date().getTime() - this.reviewStartTime!.getTime()) / 1000;
    } else {
      this.reviewDuration +=
        (new Date().getTime() - this.reviewStartTime!.getTime()) / 1000;
    }
    data.reviewDuration = this.reviewDuration;
    console.log(
      "The final reviewDuration (seconds) due to saving is: ",
      this.reviewDuration,
    );
    data.reviewWeekDay = new Date().toLocaleDateString("de", {
      weekday: "long",
    });
    console.log("The document was processed on a ", data.reviewWeekDay);

    // save the subscription name (queue), the ackId and messageId pertaining to a corresponding Pub/Sub message
    data.queue = this.queue;
    data.pubsubMessageId = this.messageId;
    data.pubsubAckId = this.ackId;

    // If the document is rejected, store it in the rejectedResultBucket and add both storno and reviewedResult keys if available.
    if (rejectReason && this.rejectedResultBucket) {
      data.storno = rejectReason;
      data.reviewedResult = content;
      content = JSON.stringify({ data });
      outputBucket = this.rejectedResultBucket;
    } else if (this.reviewedResultBucket) {
      data.reviewedResult = content;
      content = JSON.stringify({ data });
      outputBucket = this.reviewedResultBucket;
    } else {
      outputBucket = this.resultBucket;
    }

    // Fixed: Properly encode path components separately to avoid encoding slashes
    const pathComponents = newResultObjectName.split("/");
    const encodedPath = pathComponents
      .map((component) => encodeURIComponent(component))
      .join("/");
    const gcsUrl = `https://storage.googleapis.com/${outputBucket}/${encodedPath}`;

    await fetchRetry(gcsUrl, {
      method: "PUT",
      headers: {
        Authorization: "Bearer " + accessToken,
        "Content-Type": "application/json",
      },
      body: content,
    });

    // publish the reviewResult to Pub/Sub topic (can also be storno)
    var pubsubMessage = {
      document_id: data.documentId,
      processor_type: data.processorType,
      outputGcsDestination: `gs://${outputBucket}/${encodedPath}`,
    };
    console.log("PUBSUB MESSAGE", pubsubMessage);

    // acknowledge Pub/Sub message
    const response = await fetchRetry(
      `https://pubsub.googleapis.com/v1/${this.subscriptionName}:acknowledge`,
      {
        method: "POST",
        headers: {
          Authorization: "Bearer " + accessToken,
        },
        body: JSON.stringify({ ackIds: [this.ackId] }),
      },
    );

    console.warn("The messageId and ackId are: ", this.messageId, this.ackId);
    console.warn("Document ID: ", data.documentId);
    console.warn("ACKTEST Pub/Sub acknowledge response: ", response);

    url = data.callback_url;
    console.log("Triggering callback URL: ", url);

    // Prepare the response data
    const responseData = {
      status: "success",
      processed_data: pubsubMessage,
    };

    // Call callback_url of Workflow with graceful error handling
    try {
      await fetchRetry(url, {
        method: "POST",
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
        //body: JSON.stringify(this.toJson(newResultObjectName)),
        body: JSON.stringify(responseData),
      });
      console.log("Callback URL triggered successfully");
    } catch (callbackError) {
      console.warn(
        "Callback URL request failed, but document was saved successfully:",
        callbackError,
      );

      // Log the callback failure for monitoring
      const errorMessage =
        callbackError instanceof Error
          ? callbackError.message
          : String(callbackError);
      console.error("Callback URL details:", {
        url: url,
        error: errorMessage,
        timestamp: new Date().toISOString(),
        documentId: data.documentId,
        messageId: data.messageId,
      });

      // Document was successfully saved - callback failure should not stop the process
      // The workflow may have expired or been cancelled, but the document processing is complete
    }

    // stop extending acknowledgement deadline
    if (this.extendAckIdWorker) {
      this.extendAckIdWorker.terminate();
    }
  }

  toJson(newResultObjectName: string): any {
    return {
      resultBucket: this.resultBucket,
      resultPrefix: this.resultPrefix,
      resultObject: newResultObjectName,
    };
  }
}
